import { ITruFormWidgetData } from "@pageBuilder/typings";
import { WidgetRendererBase } from "../widget-renderer-base";
import { TruFormWidgetScriptRenderer } from "./truforms-widget-script-renderer";
import { IRenderingCommonContext } from "@commonRendering/types";

export class TruFormWidgetRenderer extends WidgetRendererBase<ITruFormWidgetData> {

    private readonly scriptRenderer: TruFormWidgetScriptRenderer;
    private readonly APP_CALLER_ID = "data-caller-id";

    constructor(renderingCommonContext: IRenderingCommonContext) {
        super(renderingCommonContext);
        this.scriptRenderer = new TruFormWidgetScriptRenderer(renderingCommonContext);
    }

    public createNewInternal(): HTMLElement {
        const element = document.createElement("div");
        element.setAttribute("data-widget", "truFormWidget");
        const content = document.createElement("div");
        content.classList.add("widget-content");
        this.renderPlaceholder(content);
        element.appendChild(content);

        return element;
    }

    protected renderInternal(element: HTMLElement, data: ITruFormWidgetData): HTMLElement {
        const content = element.querySelector<HTMLElement>(".widget-content");

        this.renderStyles(element, data);

        if (!content) {
            return element;
        }

        if (!this.isNeedRerender(element, data)) {
            return element;
        }

        this.scriptRenderer.renderCommon(this.renderedDocument);

        this.clearContent(content);
        if (data.formId) {
            const appDiv = document.createElement("div");
            appDiv.classList.add("tru-forms-app-caller");
            const formAppId = `tfb-${data?.formId}`;
            appDiv.setAttribute("tru-forms-app-id", formAppId);
            if (data.shouldInheritCommonStyles) {
                appDiv.setAttribute("tru-forms-should-inherit-styles", "true");
            }
            if (data.excludeFromGdpr) {
                appDiv.setAttribute("tru-forms-exclude-from-gdpr", "true");
            }
            content.appendChild(appDiv);
            element.appendChild(content);

            if (data?.id) {
                const callerId = `app-caller-${data?.id}`;
                appDiv.setAttribute(this.APP_CALLER_ID, callerId);

                this.scriptRenderer.renderLocal(element);
            }
        } else {
            this.renderPlaceholder(content);
        }

        return element;
    }

    protected renderStyles(element: HTMLElement, data: ITruFormWidgetData): void {
        this.renderBaseStyles(element, data);
    }

    private renderBaseStyles(element: HTMLElement, data: ITruFormWidgetData): void {
        this.animationRenderer.setAnimationStyles(element, data.style.animation);
    }

    private isNeedRerender(element: HTMLElement, data: ITruFormWidgetData): boolean {
        const oldData = this.getData(element);

        if (oldData?.formId !== data.formId || oldData?.id !== data.id) {
            return true;
        }

        const appCaller = element.querySelector(".tru-forms-app-caller");
        if (appCaller?.getAttribute(this.APP_CALLER_ID) !== `app-caller-${data?.id}`) {
            return true;
        }

        if(oldData?.shouldInheritCommonStyles || oldData?.shouldInheritCommonStyles !== data.shouldInheritCommonStyles) {
            return true;
        }

        return !element.querySelector("script");
    }

    private renderPlaceholder(content: HTMLElement): void {
        const placeholder = document.createElement("div");
        placeholder.classList.add("not-available");
        content.appendChild(placeholder);
        return;
    }

    private clearContent(content: HTMLElement): void {
        while (content.firstChild) {
            content.removeChild(content.firstChild);
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    protected override cleanupInternal(element: HTMLElement): void {
        // nothing
    }

    protected override getContentSelectorsInternal(element: HTMLElement): string[] {
        return [`#${element.id}`];
    }

}
